import { Route, Redirect } from "react-router-dom";
import { useEffect } from "react";
import jwt from "jwt-decode";
import { roles, useAuth } from "./auth";
import { useGetMyFeatures } from "../utils/react-query/queryFeatureFlags/queries";
import { warningToast } from "./toasts";
import { FEATURE_FLAG_TOAST_MESSAGES } from "./featureFlagMessages";

/**
 * Route guard enforcing role access and an optional feature flag.
 * @param {object} props
 * @param {Function} props.render Component rendered when access is granted.
 * @param {number[] | number[][]} props.accessLevel Allowed role ids (or groups).
 * @param {string} [props.path] Route path passed through to Route.
 * @param {string} [props.requiredFeature] Feature flag key; skips validation when omitted.
 * @param {string} [props.featureFallbackPath] Redirect target when the flag is off.
 */
function PrivateRoute({
  render: Component,
  accessLevel,
  requiredFeature = undefined,
  featureFallbackPath = "/",
  ...rest
}) {
  const { authToken } = useAuth();
  const decoded = authToken && jwt(authToken);
  let isAllowed = false;

  if (Array.isArray(accessLevel)) {
    if (typeof accessLevel[0] === "number") {
      isAllowed = decoded && accessLevel.includes(decoded.role_id);
    } else {
      isAllowed =
        decoded && accessLevel.some((level) => level.includes(decoded.role_id));
    }
  }

  const shouldValidateFeature =
    Boolean(requiredFeature) &&
    Boolean(decoded) &&
    (decoded.role_id === roles.supplier ||
      decoded.role_id === roles.salesperson ||
      decoded.role_id === roles.employee) &&
    isAllowed;

  const { data: activeFeatures = [], isLoading: isLoadingFeatures } =
    useGetMyFeatures(shouldValidateFeature);

  const hasRequiredFeature = !shouldValidateFeature
    ? true
    : activeFeatures.some((feature) => feature.key === requiredFeature);

  useEffect(() => {
    if (shouldValidateFeature && !isLoadingFeatures && !hasRequiredFeature) {
      warningToast(
        FEATURE_FLAG_TOAST_MESSAGES[requiredFeature] ||
          "El módulo no está disponible para tu proveedor"
      );
    }
  }, [
    shouldValidateFeature,
    isLoadingFeatures,
    hasRequiredFeature,
    requiredFeature,
  ]);

  return (
    <Route
      {...rest}
      render={(props) =>
        !isAllowed ? (
          <Redirect to="/" />
        ) : shouldValidateFeature &&
          isLoadingFeatures ? null : hasRequiredFeature ? (
          <Component {...props} />
        ) : (
          <Redirect to={featureFallbackPath} />
        )
      }
    />
  );
}

export default PrivateRoute;
